import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Briefcase } from 'lucide-react';
import { api } from '../../../lib/api';
import ProjectStatusChip from '../../../components/crm/ProjectStatusChip';
import EmptyState from '../../../components/crm/EmptyState';
import NewProjectDialog from '../../../components/admin/NewProjectDialog';

export default function ProjectsTab({ account }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/accounts/${account.id}/projects`);
      setProjects(res.projects || []);
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const onCreated = (project) => {
    setProjects((prev) => [project, ...prev]);
    setDialogOpen(false);
    if (project?.id) navigate(`/admin/projects/${project.id}`);
  };

  if (loading && projects.length === 0) {
    return <div className="animate-pulse h-16 rounded-lg bg-white/[0.03]" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-gray-500">{projects.length} project{projects.length === 1 ? '' : 's'}</div>
        <button
          data-testid="account-detail-new-project"
          onClick={() => setDialogOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900"
        >
          <Plus className="w-3 h-3" /> New project
        </button>
      </div>

      <NewProjectDialog
        open={dialogOpen}
        account={account}
        onClose={() => setDialogOpen(false)}
        onCreated={onCreated}
      />

      {projects.length === 0 ? (
        <EmptyState icon={Briefcase} title="No projects yet" description="Auto-created when this account transitions to contracted. Create one manually above." />
      ) : (
        <div className="space-y-2">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/admin/projects/${p.id}`)}
              className="w-full text-left p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[13px] text-white font-medium truncate">{p.name}</div>
                  <div className="flex items-center gap-x-3 gap-y-0.5 mt-0.5 flex-wrap text-[11px] text-gray-500">
                    <span className="uppercase tracking-wider">{p.type}</span>
                    {p.targetDeliveryDate && <span>target {p.targetDeliveryDate}</span>}
                    {p.actualDeliveryDate && <span>delivered {String(p.actualDeliveryDate).slice(0, 10)}</span>}
                  </div>
                </div>
                <ProjectStatusChip status={p.status} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
